import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
} from '@mui/material';
import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Scrollbar } from 'src/components/atoms/scrollbar';
import { DataStatusIndicator } from 'src/components/molecules/data-status-indicator/data-status-indicator';
import { useBookingRulesByTokens } from 'src/features/booking-rules/api/hooks/use-booking-rules-by-tokens';
import { SpaceType } from 'src/features/booking-rules/api/types/response/booking-rule.response';
import { useSpaceGroupsByTokens } from 'src/features/space-groups/api/hooks/use-space-groups-by-tokens';
import { SetSortController } from 'src/hooks/use-sort-controller';
import { SortOrder } from 'src/types/enum/sort-order';
import { SortController } from 'src/types/interfaces/sort-controller';
import { TableColumn } from 'src/types/interfaces/table-column';
import { handleUpdateSort } from 'src/utils/sort/handle-update-sort';
import { EmployeeGroupResponse } from '../../api/types/response/employee-group.response';
import {
  EmployeesGroupActionsSlotPayload,
  EmployeesGroupsTableRow,
} from './employees-groups-table-row';

interface Props {
  data: EmployeeGroupResponse[];
  isLoading: boolean;
  error: string | undefined;
  sortController: SortController;
  setSortController: SetSortController;
  handleChangeSort: (sort: string[]) => void;
  actionsSlot: (payload: EmployeesGroupActionsSlotPayload) => ReactNode;
}

export const EmployeesGroupsTable = ({
  data,
  isLoading,
  error,
  sortController,
  setSortController,
  handleChangeSort,
  actionsSlot,
}: Props) => {
  const { t } = useTranslation('employeesGroups');

  const spaceGroupsTokens = Array.from(
    new Set(
      data
        ?.flatMap(({ spaceGroupsTokens }) => spaceGroupsTokens)
        .filter(token => token !== undefined) ?? [],
    ),
  );

  const { results: spaceGroups } = useSpaceGroupsByTokens(spaceGroupsTokens);

  const getSpaceGroupsNames = (spaceGroupsTokens: string[]) => {
    return spaceGroupsTokens
      .map(token =>
        spaceGroups?.hits?.find(
          ({ token: spaceGroupToken }) => spaceGroupToken === token,
        ),
      )
      .filter(spaceGroup => spaceGroup !== undefined)
      .map(({ name }) => name);
  };

  const bookingRulesTokens = Array.from(
    new Set(
      data?.map(({ bookingRules }) => bookingRules?.map(({ token }) => token)),
    ),
  )
    .flat()
    .filter(token => token !== undefined);

  const { results: allBookingRules } =
    useBookingRulesByTokens(bookingRulesTokens);

  const getBookingRules = (
    bookingRulesTokens: { token: string; spaceType: SpaceType }[],
  ) => {
    return bookingRulesTokens.map(({ token, spaceType }) => {
      const bookingRule = allBookingRules?.hits?.find(
        ({ token: bookingRuleToken }) => bookingRuleToken === token,
      );

      return {
        name: bookingRule?.name ?? '',
        spaceType,
      };
    });
  };

  const columns: TableColumn[] = [
    {
      id: 'isAssignedToAnySpaceGroupOrBookingRules',
      label: '',
      isSortable: false,
    },
    {
      id: 'name',
      label: t('common.table.columns.name'),
      isSortable: true,
    },
    {
      id: 'spacesGroup',
      label: t('common.table.columns.spacesGroup'),
      isSortable: false,
    },
    {
      id: 'bookingRules',
      label: t('common.table.columns.bookingRules'),
      isSortable: false,
    },
    {
      id: 'numberOfEmployees',
      label: t('common.table.columns.numberOfEmployees'),
      isSortable: false,
    },
    {
      id: 'isDefault',
      label: t('common.table.columns.isDefault'),
      isSortable: false,
    },
  ];

  return (
    <Box
      sx={{
        display: 'flex',
        flex: 1,
        flexDirection: 'column',
      }}>
      <Scrollbar autoHide={false}>
        <Table sx={{ minWidth: 800 }}>
          <TableHead>
            <TableRow>
              {columns.map(column =>
                column.isSortable ? (
                  <TableCell key={column.id}>
                    <TableSortLabel
                      active={true}
                      direction={
                        sortController.sortBy === column.id
                          ? sortController.sortOrder
                          : SortOrder.ASC
                      }
                      disabled={isLoading}
                      onClick={() =>
                        handleUpdateSort(
                          setSortController,
                          handleChangeSort,
                          column.id,
                        )
                      }
                      key={column.id}>
                      {column.label}
                    </TableSortLabel>
                  </TableCell>
                ) : (
                  <TableCell key={column.id}>{column.label}</TableCell>
                ),
              )}

              <TableCell />
            </TableRow>
          </TableHead>

          <TableBody>
            {data?.map(
              ({
                token,
                name,
                employeesTokens,
                isDefault,
                bookingRules,
                spaceGroupsTokens,
              }) => {
                return (
                  <EmployeesGroupsTableRow
                    key={token}
                    token={token}
                    name={name}
                    spaceGroupsNames={getSpaceGroupsNames(
                      spaceGroupsTokens ?? [],
                    )}
                    bookingRules={getBookingRules(
                      bookingRules?.map(({ token, spaceType }) => ({
                        token,
                        spaceType,
                      })) ?? [],
                    )}
                    numberOfEmployees={employeesTokens.length}
                    isDefault={isDefault}
                    actionsSlot={actionsSlot}
                  />
                );
              },
            )}
          </TableBody>
        </Table>
      </Scrollbar>

      <DataStatusIndicator
        dataLength={data?.length}
        error={error}
        isFetching={isLoading}
      />
    </Box>
  );
};
